// Role-based menu permissions and configurations
import {
	AppstoreOutlined,
	BarChartOutlined,
	BookOutlined,
	DashboardOutlined,
	FileTextOutlined,
	HeartOutlined,
	MessageOutlined,
	QuestionCircleOutlined,
	SafetyOutlined,
	SettingOutlined,
	UserOutlined,
} from '@ant-design/icons';

import React from 'react';
import { UserRole } from '../types';

export interface MenuItemConfig {
	key: string;
	label: string | React.ReactNode;
	icon: React.ReactNode;
	onClick: () => void;
	roles: UserRole[];
	className?: string;
	disabled?: boolean;
	hidden?: boolean;
	badge?: React.ReactNode;
}

const ALL_MENU_ITEMS: MenuItemConfig[] = [
	{
		key: '/dashboard',
		label: 'Dashboard',
		icon: React.createElement(DashboardOutlined),
		onClick: () => {},
		roles: ['super_admin', 'content_manager', 'support', 'analyst'],
	},
	{
		key: '/analytics',
		label: 'Analytics',
		icon: React.createElement(BarChartOutlined),
		onClick: () => {},
		roles: ['super_admin', 'analyst'],
	},
	{
		key: '/users',
		label: 'Users',
		icon: React.createElement(UserOutlined),
		onClick: () => {},
		roles: ['super_admin', 'support'],
	},
	{
		key: '/pets',
		label: 'Pets',
		icon: React.createElement(HeartOutlined),
		onClick: () => {},
		roles: ['super_admin', 'support'],
	},
	{
		key: '/contacts',
		label: 'Contact Messages',
		icon: React.createElement(MessageOutlined),
		onClick: () => {},
		roles: ['super_admin', 'support'],
	},
	{
		key: '/settings',
		label: 'Settings',
		icon: React.createElement(SettingOutlined),
		onClick: () => {},
		roles: ['super_admin'],
	},
	{
		key: '/admin/resources',
		label: 'Resources',
		icon: React.createElement(FileTextOutlined),
		onClick: () => {},
		roles: ['super_admin', 'content_manager'],
	},
	{
		key: '/admin/training',
		label: 'Training',
		icon: React.createElement(BookOutlined),
		onClick: () => {},
		roles: ['super_admin', 'content_manager'],
	},
	{
		key: '/admin/household-safety',
		label: 'Household Safety',
		icon: React.createElement(SafetyOutlined),
		onClick: () => {},
		roles: ['super_admin', 'content_manager'],
	},
	{
		key: '/admin/breeds',
		label: 'Breeds',
		icon: React.createElement(AppstoreOutlined),
		onClick: () => {},
		roles: ['super_admin', 'content_manager'],
	},
	{
		key: '/admin/faq',
		label: 'FAQs',
		icon: React.createElement(QuestionCircleOutlined),
		onClick: () => {},
		roles: ['super_admin', 'content_manager'],
	},
	{
		key: '/admin/blog',
		label: 'Blogs',
		icon: React.createElement(FileTextOutlined),
		onClick: () => {},
		roles: ['super_admin', 'content_manager'],
	},
	{
		key: '/profile',
		label: 'Profile',
		icon: React.createElement(UserOutlined),
		onClick: () => {},
		roles: ['super_admin', 'content_manager', 'support', 'analyst'],
	},
];

const filterVisibleItems = (items: MenuItemConfig[]): MenuItemConfig[] =>
	items.filter(item => !item.hidden);

const buildMenuForRole = (role: UserRole): MenuItemConfig[] =>
	filterVisibleItems(ALL_MENU_ITEMS.filter(item => item.roles.includes(role)));

export const MENU_ITEMS: Record<UserRole, MenuItemConfig[]> = {
	super_admin: buildMenuForRole('super_admin'),
	content_manager: buildMenuForRole('content_manager'),
	support: buildMenuForRole('support'),
	analyst: buildMenuForRole('analyst'),
	patient: [],
};

export const getMenuItemsForRole = (role: UserRole): MenuItemConfig[] => {
	return MENU_ITEMS[role] || [];
};

export const hasMenuAccess = (userRole: UserRole, menuKey: string): boolean => {
	const roleMenuItems = getMenuItemsForRole(userRole);
	return roleMenuItems.some(item => item.key === menuKey);
};

export const getAccessibleRoutes = (role: UserRole): string[] => {
	const roleMenuItems = getMenuItemsForRole(role);
	const baseRoutes = roleMenuItems.map(item => item.key);
	const subRoutes: string[] = [];

	baseRoutes.forEach(route => {
		switch (route) {
			case '/users':
				subRoutes.push('/users/create', '/users/edit/');
				break;
			case '/pets':
				subRoutes.push('/pets/create', '/pets/edit/');
				break;
			case '/admin/resources':
				subRoutes.push('/admin/resources/create', '/admin/resources/edit/');
				break;
			case '/admin/training':
				subRoutes.push('/admin/training/create', '/admin/training/edit/');
				break;
			case '/admin/household-safety':
				subRoutes.push('/admin/household-safety/create', '/admin/household-safety/edit/');
				break;
			case '/admin/breeds':
				subRoutes.push('/admin/breeds/create', '/admin/breeds/edit/');
				break;
			case '/admin/faq':
				subRoutes.push('/admin/faq/create', '/admin/faq/edit/');
				break;
			case '/admin/blog':
				subRoutes.push('/admin/blog/create', '/admin/blog/edit/');
				break;
			default:
				break;
		}
	});

	return [...baseRoutes, ...subRoutes];
};
